import type { MutableRefObject } from "react";
import { Fragment, type ReactNode, useCallback, useState } from "react";
import { noop } from "lodash";
import { usePopper } from "react-popper";
import { t } from "@/i18n-js/instance";
import { Combobox } from "@circle-react-uikit/Combobox";
import { Portal } from "../Portal";
import { defaultDisplayValue } from "./defaultDisplayValue";

export interface SearchableSelectDropdownProps<T = unknown> {
  button: ReactNode;
  buttonRef?: MutableRefObject<HTMLButtonElement | null>;
  children: ReactNode;
  displayValue?: (option: T) => string;
  onChange?: (value: T) => void;
  onClose?: () => void;
  onFilter?: (value: string) => void;
  optionsClassName?: string;
  placeholder?: string;
  shouldRenderPortal?: boolean;
  value?: T;
}

const SearchableSelectDropdownComponent = <T,>({
  button,
  buttonRef,
  children,
  displayValue = defaultDisplayValue,
  onChange = noop,
  onClose = noop,
  onFilter = noop,
  optionsClassName,
  placeholder = t("select_an_option"),
  value,
  shouldRenderPortal = false,
}: SearchableSelectDropdownProps<T>) => {
  const [referenceElement, setReferenceElement] =
    useState<HTMLButtonElement | null>(null);
  const [popperElement, setPopperElement] = useState<HTMLUListElement | null>(
    null,
  );
  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement: "bottom",
    strategy: "fixed",
  });

  const combinedRef = useCallback(
    (node: HTMLButtonElement | null) => {
      setReferenceElement(node);
      if (buttonRef) {
        buttonRef.current = node;
      }
    },
    [buttonRef],
  );

  const Wrapper = shouldRenderPortal ? Portal : Fragment;

  return (
    <Combobox value={value} onChange={onChange}>
      <Combobox.Button ref={combinedRef}>{button}</Combobox.Button>
      <Wrapper className="relative z-[1065]">
        <Combobox.Options
          ref={setPopperElement}
          {...(shouldRenderPortal
            ? {
                style: styles.popper,
                ...attributes.popper,
              }
            : {})}
          onClose={onClose}
          className={optionsClassName}
        >
          <Combobox.Input
            displayValue={displayValue}
            onChange={event => onFilter(event.target.value)}
            placeholder={placeholder}
          />
          <hr className="border-primary" />
          <Combobox.OptionsScrollableContainer>
            {children}
          </Combobox.OptionsScrollableContainer>
        </Combobox.Options>
      </Wrapper>
    </Combobox>
  );
};

export const SearchableSelectDropdown = Object.assign(
  SearchableSelectDropdownComponent,
  {
    EmptyOption: Combobox.EmptyOption,
    Option: Combobox.Option,
  },
);
